/**
 * Canvas-based share card renderer — "Trophy Card" layout (iter40 redesign).
 *
 * Tester picked Option A from the iter40 design choices:
 *   • Big bold "I JUST CRUSHED IT" / "ROZWALIŁEM/ROZWALIŁAM TO" headline
 *     (gender-aware Polish — derived from the player's selected avatar:
 *      yogi → female, runner/athlete → male; null → male default).
 *   • World name + level name (TRANSLATED — no English/Polish mixing).
 *   • Massive centred time read-out in a trophy medallion.
 *   • Stat row underneath: coins · calories · steps · streak (all
 *     translated, all using correct Polish noun-case forms).
 *   • "Pobierz Fitjong · fitjong.co.uk" footer for shareability.
 *
 * Renders a 1080×1080 PNG suitable for Instagram / WhatsApp / Twitter /
 * Facebook feed. (A 1080×1920 IG-Story variant lives in renderShareStory.)
 *
 * Returns a base64 PNG dataURL — caller is responsible for writing it to
 * disk (native) or feeding it to navigator.share (web).
 *
 * We deliberately avoid html2canvas / html-to-image: a pure-canvas render
 * is ~5kB extra in the bundle vs ~70kB+ and works the same in WebView on
 * older Android phones where html-to-image has font-loading quirks.
 */

import i18n from "../i18n";

function fmtTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

/**
 * Resolve the gender-aware "crushed it" headline. Polish marks past-tense
 * verbs for the speaker's gender — "Rozwaliłem" (m) vs "Rozwaliłam" (f).
 * We derive gender from the player's selected avatar (yogi → female).
 * English has no gender marking → both keys resolve to the same string.
 */
function crushedLine(t, selectedAvatar) {
  const female = selectedAvatar === "yogi";
  return female ? t("play.share_crushed_female") : t("play.share_crushed_male");
}

/**
 * @param {Object} opts
 * @param {boolean} opts.won
 * @param {number} opts.timeSec
 * @param {number} opts.coins
 * @param {number} opts.calories
 * @param {number} [opts.steps]            — today's step count
 * @param {number} [opts.streak]           — day-N streak
 * @param {string} [opts.worldTitle]       — already-translated, e.g. "Z kanapy do aktywności"
 * @param {string} [opts.levelName]        — already-translated, e.g. "Krok 7"
 * @param {string} [opts.selectedAvatar]   — drives gender for the PL headline
 * @returns {string} PNG dataURL
 */
export function renderShareCard({
  won,
  timeSec,
  coins,
  calories,
  steps = 0,
  streak = 0,
  worldTitle = "",
  levelName = "",
  selectedAvatar = null,
}) {
  const t = i18n.t.bind(i18n);
  const SIZE = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");

  // ── 1. Background: deep green gradient like an "evening at the gym" feel.
  const bg = ctx.createLinearGradient(0, 0, SIZE, SIZE);
  bg.addColorStop(0, "#2C3A2E");
  bg.addColorStop(0.55, "#3F5042");
  bg.addColorStop(1, "#5F7161");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // ── 2. Subtle staggered tile pattern (low alpha cream) for texture.
  const TILE_W = 110;
  const TILE_H = 145;
  const GAP_X = 18;
  const GAP_Y = 22;
  const COLS = Math.ceil(SIZE / (TILE_W + GAP_X)) + 1;
  const ROWS = Math.ceil(SIZE / (TILE_H + GAP_Y)) + 1;
  ctx.save();
  ctx.translate(-30, -30);
  for (let r = 0; r < ROWS; r++) {
    const offset = r % 2 === 0 ? 0 : (TILE_W + GAP_X) / 2;
    for (let c = 0; c < COLS; c++) {
      const x = offset + c * (TILE_W + GAP_X);
      const y = r * (TILE_H + GAP_Y);
      ctx.fillStyle = "rgba(247, 245, 240, 0.045)";
      roundRect(ctx, x, y, TILE_W, TILE_H, 14);
      ctx.fill();
      ctx.fillStyle = "rgba(247, 245, 240, 0.025)";
      roundRect(ctx, x + 6, y + 6, TILE_W - 12, TILE_H * 0.55, 10);
      ctx.fill();
    }
  }
  ctx.restore();

  // ── 3. Top header — Fitjong wordmark + trophy emoji.
  ctx.textAlign = "center";
  ctx.fillStyle = "#F7F5F0";
  ctx.font = "700 64px 'Outfit', 'Segoe UI', Arial, sans-serif";
  ctx.fillText("FITJONG", SIZE / 2, 110);
  // Accent trophy circle
  ctx.fillStyle = "#E8A365";
  ctx.beginPath();
  ctx.arc(SIZE / 2 + 200, 92, 12, 0, Math.PI * 2);
  ctx.fill();

  // ── 4. Decorative bar above the headline.
  ctx.fillStyle = "rgba(247,245,240,0.45)";
  ctx.fillRect(SIZE / 2 - 140, 180, 280, 2);

  // ── 5. The gender-aware "I JUST CRUSHED IT" headline.
  ctx.fillStyle = "#F7F5F0";
  ctx.font = "800 56px 'Outfit', 'Segoe UI', Arial, sans-serif";
  ctx.fillText(
    won ? crushedLine(t, selectedAvatar) : t("play.share_session_done"),
    SIZE / 2,
    240,
  );
  ctx.fillStyle = "rgba(247,245,240,0.45)";
  ctx.fillRect(SIZE / 2 - 140, 270, 280, 2);

  // ── 6. World + level name (translated, NO English leak).
  if (worldTitle) {
    ctx.fillStyle = "#E8A365";
    ctx.font = "600 34px 'Outfit', 'Segoe UI', Arial, sans-serif";
    ctx.fillText(worldTitle.toUpperCase(), SIZE / 2, 330);
  }
  if (levelName) {
    ctx.fillStyle = "#F7F5F0";
    ctx.font = "300 52px 'Outfit', 'Segoe UI', Arial, sans-serif";
    ctx.fillText(levelName, SIZE / 2, 395);
  }

  // ── 7. Trophy medallion — cream pill with the big time read-out.
  const MED_W = 540;
  const MED_H = 220;
  const MED_X = (SIZE - MED_W) / 2;
  const MED_Y = 450;
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.35)";
  ctx.shadowBlur = 36;
  ctx.shadowOffsetY = 14;
  // Cream body
  const medGrad = ctx.createLinearGradient(MED_X, MED_Y, MED_X, MED_Y + MED_H);
  medGrad.addColorStop(0, "#FCF8EE");
  medGrad.addColorStop(1, "#EFE6CF");
  ctx.fillStyle = medGrad;
  roundRect(ctx, MED_X, MED_Y, MED_W, MED_H, 32);
  ctx.fill();
  ctx.restore();
  // Inner border
  ctx.strokeStyle = "rgba(95,113,97,0.18)";
  ctx.lineWidth = 3;
  roundRect(ctx, MED_X + 8, MED_Y + 8, MED_W - 16, MED_H - 16, 26);
  ctx.stroke();
  // Time number
  ctx.fillStyle = "#2C332A";
  ctx.font = "300 170px 'Outfit', 'Segoe UI', Arial, sans-serif";
  ctx.fillText(fmtTime(timeSec), SIZE / 2, MED_Y + 152);
  // "Clear time" small caption
  ctx.fillStyle = "rgba(44,51,42,0.55)";
  ctx.font = "500 24px 'Outfit', 'Segoe UI', Arial, sans-serif";
  ctx.fillText(t("play.share_clear_time"), SIZE / 2, MED_Y + 192);

  // ── 8. Stat row — 4 stats: coins · calories · steps · streak (all PL/EN).
  const statsY = MED_Y + MED_H + 110;
  const showSteps = steps > 0;
  const showStreak = streak > 0;
  const visible = [
    { value: `+${coins}`, label: t("play.share_coins_label"), color: "#E8A365" },
    { value: `+${calories}`, label: t("play.share_calories_label"), color: "#FFD7A6" },
    showSteps ? { value: steps.toLocaleString(i18n.language), label: t("hud.steps").toUpperCase(), color: "#9CB39E" } : null,
    showStreak ? { value: `${streak}`, label: t("play.share_streak_label", { n: streak }).toUpperCase(), color: "#F4B97F" } : null,
  ].filter(Boolean);
  const slotW = SIZE / visible.length;
  visible.forEach((s, i) => {
    const cx = slotW * (i + 0.5);
    ctx.fillStyle = s.color;
    ctx.font = "700 64px 'Outfit', 'Segoe UI', Arial, sans-serif";
    ctx.fillText(s.value, cx, statsY);
    ctx.fillStyle = "rgba(247,245,240,0.78)";
    ctx.font = "600 22px 'Outfit', 'Segoe UI', Arial, sans-serif";
    ctx.fillText(s.label, cx, statsY + 42);
  });

  // ── 9. Footer tagline.
  ctx.fillStyle = "rgba(247,245,240,0.85)";
  ctx.font = "500 28px 'Outfit', 'Segoe UI', Arial, sans-serif";
  ctx.fillText(t("play.share_footer_tagline"), SIZE / 2, SIZE - 100);
  ctx.fillStyle = "#E8A365";
  ctx.font = "700 26px 'Outfit', 'Segoe UI', Arial, sans-serif";
  ctx.fillText(t("play.share_get_app"), SIZE / 2, SIZE - 56);

  return canvas.toDataURL("image/png");
}

/**
 * 1080×1920 vertical variant for Instagram Stories / Snapchat / TikTok.
 * Same content, taller layout — extra breathing room top/bottom.
 */
export function renderShareStory(opts) {
  // Use the same data but render at 9:16. Implemented in iter41 if needed.
  // For now we expose the square one — every social platform accepts it.
  return renderShareCard(opts);
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/** Convert a PNG dataURL to a Blob — used by the Web Share API path. */
export function dataUrlToBlob(dataUrl) {
  const parts = dataUrl.split(",");
  const mime = /data:(.*?);base64/.exec(parts[0])?.[1] || "image/png";
  const bin = atob(parts[1]);
  const len = bin.length;
  const u8 = new Uint8Array(len);
  for (let i = 0; i < len; i++) u8[i] = bin.charCodeAt(i);
  return new Blob([u8], { type: mime });
}
